import { useMemo, useState } from "react";

const DEFAULT_POSITIONS = [
  { id: "cmd_1", team: "Command", position: "Incident Commander", roleHint: "Commander" },
  { id: "cmd_2", team: "Command", position: "Team Leader", roleHint: "Team Leader" },
  { id: "entry_1", team: "Entry Team", position: "Point / Entry 1", roleHint: "Entry" },
  { id: "entry_2", team: "Entry Team", position: "Entry 2", roleHint: "Entry" },
  { id: "entry_3", team: "Entry Team", position: "Breacher", roleHint: "Breacher" },
  { id: "entry_4", team: "Entry Team", position: "Less-Lethal", roleHint: "Less-Lethal" },
  { id: "perim_1", team: "Perimeter", position: "North Containment", roleHint: "Perimeter" },
  { id: "perim_2", team: "Perimeter", position: "South Containment", roleHint: "Perimeter" },
  { id: "overwatch_1", team: "Overwatch", position: "Overwatch / Observer", roleHint: "Overwatch" },
  { id: "uas_1", team: "Drone / UAS", position: "Drone Operator", roleHint: "Drone" },
  { id: "med_1", team: "Medical", position: "Tactical Medical", roleHint: "Medical" }
];

const TEMPLATE_PRESETS = {
  "Search Warrant": DEFAULT_POSITIONS,
  "Arrest Warrant": DEFAULT_POSITIONS,
  "Barricaded Subject": [
    { id: "cmd_1", team: "Command", position: "Incident Commander", roleHint: "Commander" },
    { id: "cmd_2", team: "Command", position: "Tactical Commander", roleHint: "Commander" },
    { id: "neg_1", team: "Negotiations", position: "Primary Negotiator", roleHint: "Negotiator" },
    { id: "neg_2", team: "Negotiations", position: "Secondary Negotiator", roleHint: "Negotiator" },
    { id: "contain_1", team: "Containment", position: "Inner Containment", roleHint: "Perimeter" },
    { id: "contain_2", team: "Containment", position: "Outer Containment", roleHint: "Perimeter" },
    { id: "obs_1", team: "Overwatch", position: "Observer / Marksman", roleHint: "Overwatch" },
    { id: "uas_1", team: "Drone / UAS", position: "Drone Operator", roleHint: "Drone" },
    { id: "med_1", team: "Medical", position: "Tactical Medical", roleHint: "Medical" }
  ],
  "Surveillance": [
    { id: "cmd_1", team: "Command", position: "Case / Team Lead", roleHint: "Team Leader" },
    { id: "mobile_1", team: "Mobile Surveillance", position: "Mobile Unit 1", roleHint: "Surveillance" },
    { id: "mobile_2", team: "Mobile Surveillance", position: "Mobile Unit 2", roleHint: "Surveillance" },
    { id: "static_1", team: "Static Surveillance", position: "Static Post 1", roleHint: "Surveillance" },
    { id: "intel_1", team: "Intel", position: "Intel / Comms", roleHint: "Intel" },
    { id: "arrest_1", team: "Arrest Team", position: "Arrest Team Lead", roleHint: "Entry" }
  ]
};

function makeId(prefix = "assign") {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function getTemplatesForOperation(operation) {
  const type = operation?.operationType || operation?.missionType || "Search Warrant";
  return TEMPLATE_PRESETS[type] || DEFAULT_POSITIONS;
}

function getStatusBadge(status) {
  if (status === "Available") return "good";
  if (status === "On Operation" || status === "Assigned") return "warn";
  if (status === "Unavailable") return "danger";
  return "neutral";
}

export default function AssignmentBoard({ operations, roster, setRoster, assignments, setAssignments }) {
  const activeOperations = useMemo(
    () => operations.filter((op) => !["Closed", "Cancelled"].includes(op.status)),
    [operations]
  );

  const [selectedOperationId, setSelectedOperationId] = useState(activeOperations[0]?.id || operations[0]?.id || "");
  const [selectedOperatorId, setSelectedOperatorId] = useState("");
  const [customTeam, setCustomTeam] = useState("Entry Team");
  const [customPosition, setCustomPosition] = useState("");

  const selectedOperation = operations.find((op) => op.id === selectedOperationId) || operations[0];
  const operationAssignments = assignments.filter((item) => item.operationId === selectedOperation?.id);
  const templates = getTemplatesForOperation(selectedOperation);

  const assignedOperatorIds = new Set(operationAssignments.map((item) => item.operatorId).filter(Boolean));
  const availableRoster = roster.filter((operator) => !assignedOperatorIds.has(operator.id));
  const filled = operationAssignments.filter((item) => item.operatorId).length;
  const vacancies = Math.max(templates.length - filled, 0);

  const groupedAssignments = useMemo(() => {
    const merged = templates.map((slot) => {
      const existing = operationAssignments.find((item) => item.slotId === slot.id);
      const operator = existing ? roster.find((person) => person.id === existing.operatorId) : null;
      return {
        ...slot,
        assignmentId: existing?.id,
        operatorId: existing?.operatorId || "",
        operator,
        notes: existing?.notes || "",
        readiness: existing?.readiness || (operator ? "Assigned" : "Vacant")
      };
    });

    operationAssignments
      .filter((item) => !templates.some((slot) => slot.id === item.slotId))
      .forEach((item) => {
        merged.push({
          id: item.slotId,
          team: item.team || "Custom",
          position: item.position || "Custom Assignment",
          roleHint: item.roleHint || "",
          assignmentId: item.id,
          operatorId: item.operatorId,
          operator: roster.find((person) => person.id === item.operatorId),
          notes: item.notes || "",
          readiness: item.readiness || "Assigned",
          custom: true
        });
      });

    return merged.reduce((groups, item) => {
      if (!groups[item.team]) groups[item.team] = [];
      groups[item.team].push(item);
      return groups;
    }, {});
  }, [templates, operationAssignments, roster]);

  function updateRosterStatus(operatorId, status) {
    if (!operatorId) return;
    setRoster(roster.map((person) => (person.id === operatorId ? { ...person, status } : person)));
  }

  function assignToSlot(slot, operatorId) {
    if (!selectedOperation?.id) return alert("Select an operation first.");
    if (!operatorId) return;

    const current = operationAssignments.find((item) => item.slotId === slot.id);
    const previousOperatorId = current?.operatorId;

    const nextAssignment = {
      id: current?.id || makeId("assign"),
      operationId: selectedOperation.id,
      slotId: slot.id,
      team: slot.team,
      position: slot.position,
      roleHint: slot.roleHint || "",
      operatorId,
      readiness: "Assigned",
      notes: current?.notes || "",
      updatedAt: new Date().toISOString()
    };

    const withoutConflicts = assignments.filter(
      (item) => !(item.operationId === selectedOperation.id && (item.slotId === slot.id || item.operatorId === operatorId))
    );

    setAssignments([...withoutConflicts, nextAssignment]);
    if (previousOperatorId && previousOperatorId !== operatorId) updateRosterStatus(previousOperatorId, "Available");
    updateRosterStatus(operatorId, "On Operation");
  }

  function clearSlot(slot) {
    const existing = operationAssignments.find((item) => item.slotId === slot.id);
    if (!existing) return;
    if (!window.confirm(`Clear ${slot.position}?`)) return;
    setAssignments(assignments.filter((item) => item.id !== existing.id));
    updateRosterStatus(existing.operatorId, "Available");
  }

  function addCustomAssignment(event) {
    event.preventDefault();
    if (!selectedOperation?.id) return alert("Select an operation first.");
    if (!selectedOperatorId) return alert("Select an operator.");
    const position = customPosition.trim();
    if (!position) return alert("Enter a position name.");

    const nextAssignment = {
      id: makeId("assign"),
      operationId: selectedOperation.id,
      slotId: makeId("slot"),
      team: customTeam || "Custom",
      position,
      roleHint: "Custom",
      operatorId: selectedOperatorId,
      readiness: "Assigned",
      notes: "",
      updatedAt: new Date().toISOString()
    };

    setAssignments([
      ...assignments.filter((item) => !(item.operationId === selectedOperation.id && item.operatorId === selectedOperatorId)),
      nextAssignment
    ]);
    updateRosterStatus(selectedOperatorId, "On Operation");
    setSelectedOperatorId("");
    setCustomPosition("");
  }

  function clearOperationAssignments() {
    if (!selectedOperation?.id) return;
    if (!window.confirm("Clear all personnel assignments for this operation?")) return;
    const clearing = assignments.filter((item) => item.operationId === selectedOperation.id);
    const clearingIds = new Set(clearing.map((item) => item.operatorId));
    setAssignments(assignments.filter((item) => item.operationId !== selectedOperation.id));
    setRoster(roster.map((person) => (clearingIds.has(person.id) ? { ...person, status: "Available" } : person)));
  }

  function exportAssignments() {
    if (!selectedOperation) return;

    const esc = (value) =>
      String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const grouped = Object.entries(groupedAssignments);
    const generatedAt = new Date().toLocaleString();
    const filledCount = operationAssignments.filter((item) => item.operatorId).length;
    const vacantCount = Math.max(templates.length - filledCount, 0);

    const assignmentSections = grouped
      .map(([team, slots]) => `
        <section class="team-section">
          <div class="team-header">
            <h2>${esc(team)}</h2>
            <span>${slots.filter((slot) => slot.operator).length}/${slots.length} filled</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Position</th>
                <th>Operator</th>
                <th>Role</th>
                <th>Sub-Team</th>
                <th>Status</th>
                <th>Certs / Notes</th>
              </tr>
            </thead>
            <tbody>
              ${slots.map((slot) => `
                <tr class="${slot.operator ? "filled" : "vacant"}">
                  <td><strong>${esc(slot.position)}</strong><br><small>${esc(slot.roleHint || "Role flexible")}</small></td>
                  <td>${esc(slot.operator?.name || "VACANT")}</td>
                  <td>${esc(slot.operator?.role || slot.roleHint || "")}</td>
                  <td>${esc(slot.operator?.subTeam || "")}</td>
                  <td>${esc(slot.operator ? "Assigned" : "Vacant")}</td>
                  <td>${esc(slot.operator?.certs || slot.notes || "")}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        </section>
      `)
      .join("");

    const html = `
      <!doctype html>
      <html>
        <head>
          <title>FAST Assignment Board - ${esc(selectedOperation.title)}</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 0;
              padding: 34px;
              font-family: Arial, Helvetica, sans-serif;
              color: #101820;
              background: #f4f5f7;
            }
            .sheet {
              max-width: 1120px;
              margin: 0 auto;
              background: #fff;
              border: 1px solid #d9dde3;
              padding: 34px;
              box-shadow: 0 12px 35px rgba(0,0,0,.12);
            }
            .print-actions {
              display: flex;
              justify-content: flex-end;
              gap: 10px;
              margin-bottom: 18px;
            }
            button {
              border: 1px solid #1f2937;
              background: #101820;
              color: #fff;
              border-radius: 8px;
              padding: 10px 14px;
              font-weight: 700;
              cursor: pointer;
            }
            .header {
              display: grid;
              grid-template-columns: 1fr auto;
              gap: 18px;
              align-items: start;
              border-bottom: 4px solid #101820;
              padding-bottom: 18px;
              margin-bottom: 20px;
            }
            .eyebrow {
              color: #8a6f2d;
              font-weight: 900;
              text-transform: uppercase;
              letter-spacing: .14em;
              font-size: 12px;
              margin-bottom: 8px;
            }
            h1 {
              margin: 0;
              font-size: 30px;
              letter-spacing: -.03em;
            }
            .sub {
              margin-top: 8px;
              color: #4b5563;
              line-height: 1.4;
            }
            .stamp {
              border: 2px solid #101820;
              padding: 12px 14px;
              text-align: center;
              font-weight: 900;
              min-width: 180px;
            }
            .meta-grid, .stats-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 10px;
              margin-bottom: 18px;
            }
            .meta-box, .stat-box {
              border: 1px solid #d1d5db;
              padding: 10px;
              min-height: 62px;
              background: #fafafa;
            }
            .label {
              display: block;
              color: #6b7280;
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: .09em;
              font-weight: 900;
              margin-bottom: 5px;
            }
            .value {
              font-weight: 800;
              white-space: pre-wrap;
            }
            .stat-box b {
              display: block;
              font-size: 28px;
              line-height: 1;
            }
            .stat-box span {
              display: block;
              color: #6b7280;
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: .08em;
              font-weight: 900;
              margin-top: 6px;
            }
            .team-section {
              margin-top: 18px;
              page-break-inside: avoid;
            }
            .team-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              background: #101820;
              color: #fff;
              padding: 10px 12px;
            }
            .team-header h2 {
              margin: 0;
              font-size: 16px;
              text-transform: uppercase;
              letter-spacing: .08em;
            }
            .team-header span {
              color: #d6bd71;
              font-weight: 900;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
            }
            th, td {
              border: 1px solid #d1d5db;
              padding: 9px;
              vertical-align: top;
              text-align: left;
              font-size: 12px;
              word-wrap: break-word;
            }
            th {
              background: #eef1f5;
              color: #374151;
              text-transform: uppercase;
              font-size: 10px;
              letter-spacing: .07em;
            }
            tr.vacant td {
              background: #fff1f1;
              color: #7f1d1d;
            }
            small { color: #6b7280; }
            .footer {
              margin-top: 24px;
              padding-top: 12px;
              border-top: 1px solid #d1d5db;
              color: #6b7280;
              font-size: 11px;
              display: flex;
              justify-content: space-between;
            }
            @media print {
              body { background: #fff; padding: 0; }
              .sheet { box-shadow: none; border: 0; max-width: none; }
              .print-actions { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="sheet">
            <div class="print-actions">
              <button onclick="window.print()">Print / Save as PDF</button>
            </div>

            <div class="header">
              <div>
                <div class="eyebrow">TacOps FAST Assignment Board</div>
                <h1>${esc(selectedOperation.title || "Operation Assignment")}</h1>
                <div class="sub">${esc(selectedOperation.operationType || selectedOperation.missionType || "Operation")} • ${esc(selectedOperation.location || "No location entered")}</div>
              </div>
              <div class="stamp">FAST<br>PERSONNEL<br>ASSIGNMENT</div>
            </div>

            <div class="meta-grid">
              <div class="meta-box"><span class="label">Operation</span><div class="value">${esc(selectedOperation.title)}</div></div>
              <div class="meta-box"><span class="label">Type</span><div class="value">${esc(selectedOperation.operationType || selectedOperation.missionType)}</div></div>
              <div class="meta-box"><span class="label">Location</span><div class="value">${esc(selectedOperation.location)}</div></div>
              <div class="meta-box"><span class="label">Generated</span><div class="value">${esc(generatedAt)}</div></div>
              <div class="meta-box"><span class="label">Commander</span><div class="value">${esc(selectedOperation.commander)}</div></div>
              <div class="meta-box"><span class="label">Team Lead</span><div class="value">${esc(selectedOperation.teamLead)}</div></div>
              <div class="meta-box"><span class="label">Staging</span><div class="value">${esc(selectedOperation.staging)}</div></div>
              <div class="meta-box"><span class="label">Status</span><div class="value">${esc(selectedOperation.status)}</div></div>
            </div>

            <div class="stats-grid">
              <div class="stat-box"><b>${filledCount}</b><span>Filled Assignments</span></div>
              <div class="stat-box"><b>${vacantCount}</b><span>Vacancies</span></div>
              <div class="stat-box"><b>${templates.length}</b><span>Template Slots</span></div>
              <div class="stat-box"><b>${availableRoster.length}</b><span>Available Operators</span></div>
            </div>

            ${assignmentSections || `<div class="meta-box"><strong>No assignments found for this operation.</strong></div>`}

            <div class="footer">
              <span>TacOps by Aegis Group</span>
              <span>Generated from Personnel Assignment Board</span>
            </div>
          </div>
        </body>
      </html>
    `;

    const win = window.open("", "_blank");
    if (!win) {
      alert("Popup blocked. Allow popups and try again.");
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  }

  if (!operations.length) {
    return <section className="panel"><h2>Personnel Assignments</h2><p className="muted">Create an operation first, then build the assignment board.</p></section>;
  }

  return (
    <section>
      <div className="section-head">
        <div>
          <h2>Personnel Assignment Board</h2>
          <p className="muted">Assign operators into mission positions, sub-teams, and vacancies. This is the foundation for packets, maps, and AARs.</p>
        </div>
        <div className="assignment-actions">
          <button className="primary" onClick={exportAssignments}>Export PDF</button>
          <button className="danger-text" onClick={clearOperationAssignments}>Clear Board</button>
        </div>
      </div>

      <div className="assignment-command-strip">
        <label>
          <span>Operation</span>
          <select value={selectedOperation?.id || ""} onChange={(e) => setSelectedOperationId(e.target.value)}>
            {operations.map((op) => (
              <option key={op.id} value={op.id}>{op.title} — {op.status}</option>
            ))}
          </select>
        </label>
        <div className="assignment-stat-card tone-green"><b>{filled}</b><span>Filled</span></div>
        <div className="assignment-stat-card tone-gold"><b>{vacancies}</b><span>Vacancies</span></div>
        <div className="assignment-stat-card tone-blue"><b>{availableRoster.length}</b><span>Available</span></div>
      </div>

      <div className="assignment-layout">
        <div className="panel assignment-board-panel">
          <div className="assignment-board-head">
            <div>
              <h3>{selectedOperation?.title || "Operation"}</h3>
              <p className="muted">{selectedOperation?.operationType || selectedOperation?.missionType || "Operation"} • {selectedOperation?.location || "No location"}</p>
            </div>
            <span className="badge warn">Template: {selectedOperation?.operationType || selectedOperation?.missionType || "Default"}</span>
          </div>

          <div className="assignment-team-stack">
            {Object.entries(groupedAssignments).map(([team, slots]) => (
              <div className="assignment-team" key={team}>
                <div className="assignment-team-title">
                  <span>{team}</span>
                  <small>{slots.filter((slot) => slot.operator).length}/{slots.length} filled</small>
                </div>
                <div className="assignment-slot-grid">
                  {slots.map((slot) => (
                    <div className={`assignment-slot ${slot.operator ? "filled" : "vacant"}`} key={slot.id}>
                      <div className="assignment-slot-top">
                        <div>
                          <b>{slot.position}</b>
                          <small>{slot.roleHint || "Role flexible"}</small>
                        </div>
                        <span className={`badge ${slot.operator ? "good" : "danger"}`}>{slot.operator ? "Assigned" : "Vacant"}</span>
                      </div>

                      {slot.operator ? (
                        <div className="assigned-operator-card">
                          <strong>{slot.operator.name}</strong>
                          <small>{slot.operator.role || "No role"} • {slot.operator.subTeam || "No sub-team"}</small>
                          <small>{slot.operator.certs || "No cert notes"}</small>
                        </div>
                      ) : (
                        <div className="assignment-vacant-note">Select an available operator to fill this position.</div>
                      )}

                      <div className="assignment-slot-controls">
                        <select value={slot.operatorId || ""} onChange={(e) => assignToSlot(slot, e.target.value)}>
                          <option value="">{slot.operator ? "Change operator" : "Assign operator"}</option>
                          {roster.map((operator) => (
                            <option key={operator.id} value={operator.id}>
                              {operator.name} — {operator.role || "No role"} — {operator.status || "Unknown"}
                            </option>
                          ))}
                        </select>
                        <button className="danger-text" disabled={!slot.operator} onClick={() => clearSlot(slot)}>Clear</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <aside className="assignment-side-stack">
          <div className="panel">
            <h3>Available Operators</h3>
            <p className="muted">Operators not currently assigned to this operation.</p>
            <div className="assignment-available-list">
              {availableRoster.length === 0 ? <div className="empty">No unassigned operators for this operation.</div> : null}
              {availableRoster.map((operator) => (
                <div className="assignment-operator-row" key={operator.id}>
                  <div>
                    <b>{operator.name}</b>
                    <small>{operator.role || "No role"} • {operator.subTeam || "No sub-team"}</small>
                  </div>
                  <span className={`badge ${getStatusBadge(operator.status)}`}>{operator.status || "Unknown"}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="panel">
            <h3>Custom Assignment</h3>
            <p className="muted">Add a one-off position without changing the mission template.</p>
            <form className="assignment-custom-form" onSubmit={addCustomAssignment}>
              <label><span>Operator</span><select value={selectedOperatorId} onChange={(e) => setSelectedOperatorId(e.target.value)}><option value="">Select operator</option>{availableRoster.map((operator) => <option key={operator.id} value={operator.id}>{operator.name}</option>)}</select></label>
              <label><span>Team / Cell</span><input value={customTeam} onChange={(e) => setCustomTeam(e.target.value)} placeholder="Entry Team, Perimeter, Intel..." /></label>
              <label><span>Position</span><input value={customPosition} onChange={(e) => setCustomPosition(e.target.value)} placeholder="Driver, cover, arrest team, intel..." /></label>
              <button className="primary" type="submit">Add Assignment</button>
            </form>
          </div>
        </aside>
      </div>
    </section>
  );
}
